import React, { useEffect, useState } from 'react';
import { getVariantImage, readCachedVariantImage } from '../../services/inventoryCountsAPI';
import './ProductInfoModal.css';

interface ProductImageProps {
  readonly sku: string;
  readonly alt?: string;
  /** When set, skip the variant-image fetch and use this URL directly. */
  readonly src?: string | null;
}

function cachedImageState(sku: string, src: string | null | undefined): { url: string | null; loading: boolean } {
  if (src) return { url: src, loading: false };
  const cached = readCachedVariantImage(sku);
  if (cached !== undefined) return { url: cached, loading: false };
  return { url: null, loading: Boolean(sku) };
}

function ProductImage({ sku, alt, src }: ProductImageProps): React.ReactElement | null {
  const [imageUrl, setImageUrl] = useState<string | null>(() => cachedImageState(sku, src).url);
  const [imageLoading, setImageLoading] = useState(() => cachedImageState(sku, src).loading);

  useEffect(() => {
    if (src) {
      setImageUrl(src);
      setImageLoading(false);
      return;
    }
    if (!sku) {
      setImageUrl(null);
      setImageLoading(false);
      return;
    }
    const cached = readCachedVariantImage(sku);
    if (cached !== undefined) {
      setImageUrl(cached);
      setImageLoading(false);
      return;
    }
    let cancelled = false;
    setImageLoading(true);
    setImageUrl(null);
    getVariantImage(sku).then((url: string | null) => {
      if (!cancelled) {
        setImageUrl(url);
        setImageLoading(false);
      }
    }).catch(() => {
      if (!cancelled) {
        setImageUrl(null);
        setImageLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [sku, src]);

  if (!sku) return null;

  return (
    <div className="product-info-image-placeholder">
      {imageLoading ? (
        <span className="product-info-placeholder-text">Loading...</span>
      ) : imageUrl ? (
        <img src={imageUrl} alt={alt || sku} className="product-info-image" />
      ) : (
        <span className="product-info-placeholder-text">No image</span>
      )}
    </div>
  );
}

export default ProductImage;
